import {Col, Flex, Form, Input, Select} from 'antd';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import React from 'react';
import {useSearchParams} from 'react-router-dom';

import {CommonErrors} from '../../constants/Common';
import NumberInput from '../../shared/NumberInput';
import {formatRoundedIndian} from '../../utils/formatIndian';
import {yearSelect} from '../../utils/FormUtils/yearSelect';
import FormListComp from '../DrawerForm/Utils/FormListComp';

import {
  CarbonOffsetKey,
  CarbonOffsetLabel,
  carbonOffsetProjectTypes,
  carbonOffsetStageTypes,
  EMISSIONS_OFFSET_VALIDATION_MESSAGE,
  isValidEmissionsOffset,
  sumEmissionsOffset
} from './Constants/CarbonOffset/CarbonOffsetKeyLabel';
import {yearKeyLabel} from './Constants/IP/commonKeyLabel';
import {emissionUnits} from './interface/unitChange';

// Fields of a single project; `name` is the Form.List index
const CarbonOffsetProjectFields: React.FC<{name?: number}> = ({name = 0}) => {
  return (
    <>
      <Col className="gutter-row" span={24}>
        <Form.Item
          name={[name, CarbonOffsetKey.PROJECT_NAME]}
          label={CarbonOffsetLabel.PROJECT_NAME}
          labelCol={{span: 24}}
          rules={[
            {
              required: true,
              whitespace: true,
              message: CommonErrors.mandatory(CarbonOffsetLabel.PROJECT_NAME)
            }
          ]}
        >
          <Input placeholder={`Enter ${CarbonOffsetLabel.PROJECT_NAME}`} />
        </Form.Item>
      </Col>
      <Col className="gutter-row" span={12}>
        <Form.Item
          name={[name, CarbonOffsetKey.PROJECT_TYPE]}
          label={CarbonOffsetLabel.PROJECT_TYPE}
          labelCol={{span: 24}}
          rules={[
            {
              required: true,
              message: CommonErrors.mandatory(CarbonOffsetLabel.PROJECT_TYPE)
            }
          ]}
        >
          <Select
            className="select"
            placeholder={`Choose ${CarbonOffsetLabel.PROJECT_TYPE}`}
            options={carbonOffsetProjectTypes}
          />
        </Form.Item>
      </Col>
      <Col className="gutter-row" span={12}>
        <Form.Item
          name={[name, CarbonOffsetKey.STAGE_TYPE]}
          label={CarbonOffsetLabel.STAGE_TYPE}
          labelCol={{span: 24}}
          rules={[
            {
              required: true,
              message: CommonErrors.mandatory(CarbonOffsetLabel.STAGE_TYPE)
            }
          ]}
        >
          <Select
            className="select"
            placeholder={`Choose ${CarbonOffsetLabel.STAGE_TYPE}`}
            options={carbonOffsetStageTypes}
          />
        </Form.Item>
      </Col>
      <Col className="gutter-row" span={12}>
        <Form.Item
          name={[name, CarbonOffsetKey.EMISSIONS_OFFSET]}
          label={CarbonOffsetLabel.EMISSIONS_OFFSET}
          labelCol={{span: 24}}
          rules={[
            {
              required: true,
              message: CommonErrors.mandatory(
                CarbonOffsetLabel.EMISSIONS_OFFSET
              )
            },
            {
              validator: async (_, value) => {
                if (value === undefined || value === null || value === '') {
                  return;
                }

                if (!isValidEmissionsOffset(value)) {
                  return await Promise.reject(
                    new Error(EMISSIONS_OFFSET_VALIDATION_MESSAGE)
                  );
                }
              }
            }
          ]}
          validateFirst
        >
          <NumberInput
            placeholder={`Enter ${CarbonOffsetLabel.EMISSIONS_OFFSET}`}
            addonAfter={emissionUnits.T_CO2e}
          />
        </Form.Item>
      </Col>
      <Col className="gutter-row" span={24}>
        <Form.Item
          name={[name, CarbonOffsetKey.DESCRIPTION]}
          label={CarbonOffsetLabel.DESCRIPTION}
          labelCol={{span: 24}}
        >
          <Input.TextArea
            rows={4}
            placeholder={`Enter ${CarbonOffsetLabel.DESCRIPTION}`}
          />
        </Form.Item>
      </Col>
    </>
  );
};

const CarbonOffsetForm: React.FC<{
  data: any;
  currentStep?: number;
}> = ({data, currentStep}) => {
  const FormInstance = useFormInstance();
  const [searchParam] = useSearchParams();
  const isEdit = data?._id !== undefined;

  const yearFilter = yearSelect();

  const selectedYear = Form.useWatch(yearKeyLabel.key, {
    form: FormInstance,
    preserve: true
  });
  const projects = Form.useWatch(CarbonOffsetKey.PROJECTS, {
    form: FormInstance,
    preserve: true
  });
  const totalEmissionsOffset = sumEmissionsOffset(projects);

  return (
    <>
      {currentStep === 0 && (
        <>
          <div className="carbonOffsetFormStep">
            <div className="stepTitle">Select Fiscal Year</div>
            <div className="carbonOffsetStepSubTitle">
              Choose the fiscal year these offset projects belong to.
            </div>
          </div>
          <Col className="gutter-row">
            <Form.Item
              name={yearKeyLabel.key}
              label={yearKeyLabel.label}
              labelCol={{span: 24}}
              initialValue={searchParam.get('selectedYear')}
              rules={[
                {
                  required: true,
                  message: CommonErrors.mandatory(yearKeyLabel.label)
                }
              ]}
            >
              <Select
                placeholder={yearKeyLabel.label}
                options={yearFilter}
                disabled={isEdit}
              />
            </Form.Item>
          </Col>
        </>
      )}

      {currentStep === 1 && (
        <>
          <div className="carbonOffsetFormStep">
            <div className="stepTitle">Carbon Offset Project Details</div>
            <div className="carbonOffsetStepSubTitle">
              Add each project and the emissions it offset. The year&apos;s
              total is the sum of all projects.
            </div>
          </div>
          <FormListComp
            mainName={CarbonOffsetKey.PROJECTS}
            formComponent={<CarbonOffsetProjectFields />}
          />
          <Flex className="carbonOffsetTotal" align="center">
            <div className="totalLabel">
              {CarbonOffsetLabel.TOTAL_EMISSIONS_OFFSET} · FY {selectedYear}
            </div>
            <div className="totalValue">
              {formatRoundedIndian(totalEmissionsOffset)}{' '}
              <span className="totalUnit">{emissionUnits.T_CO2e}</span>
            </div>
          </Flex>
        </>
      )}
    </>
  );
};

export default CarbonOffsetForm;
